// ProductImageUpload.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import logger, { Logger } from '../../utils/logger';
import { Product } from '../../services/productService';


interface ImageType {
  isPreview: any;
  _id: string;
  filePath: string;
  fileName: string;
  bucketUrl: string;
  previewUrl?: string;

}


interface ProductImageUploadProps {
  existingImages: ImageType[];
  onFilesChange: (files: File[]) => void;
  onDeleteImage?: (imageId: string) => void;
  bucketUrl: string;
  showPreview?: boolean; // Add this property with optional flag
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = {
  'image/*': ['.jpeg', '.jpg', '.png']
};

const ProductImageUpload: React.FC<ProductImageUploadProps> = ({
  existingImages = [],
  onFilesChange,
  onDeleteImage,
  showPreview = false,

  bucketUrl
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const validImages = React.useMemo(() => {
    if (!existingImages || !Array.isArray(existingImages)) {
      logger.log('ProductImageUpload: existingImages is not a valid array', existingImages);
      return [];
    }

    return existingImages.filter(img =>
      img && typeof img === 'object' &&
      (
        (img.filePath && typeof img.filePath === 'string') || // Existing images
        (img.isPreview && img.previewUrl && typeof img.previewUrl === 'string') // Preview images
      )
    );
  }, [existingImages]);
  
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);


  // Generate full image URL
  const getImageUrl = (image: any) => {
    // If it's a preview image, use the URL directly
    if (image.isPreview && image.previewUrl) {
      return image.previewUrl;
    }
    
    // If image is just a string URL, return it directly
    if (typeof image === 'string') return image;
    
    // If image is an object with filePath, process it
    if (image && typeof image === 'object' && image.filePath) {
      const cleanPath = image.filePath.replace(/^\/+/, '');
      if (!bucketUrl) return '/fallback.png';
      const cleanBucket = bucketUrl.replace(/\/+$/, '');
      return `${cleanBucket}/${cleanPath}`;
    }
    
    // Otherwise return fallback
    return '/fallback.png';
  };


  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    // Handle rejected files
    rejectedFiles.forEach(({ file, errors }) => {
      if (errors[0]?.code === 'file-too-large') {
        toast.error(`${file.name} is too large (max: 5MB)`);
      } else if (errors[0]?.code === 'file-invalid-type') {
        toast.error(`${file.name} is not a valid image type`);
      }
    });

    // Process accepted files
    const newFiles = acceptedFiles.filter(file => {
      const isDuplicate = selectedFiles.some(
        existing => existing.name === file.name && existing.size === file.size
      );
      return !isDuplicate;
    });

    if (newFiles.length) {
      logger.log(`Selected ${newFiles.length} files for upload:`, newFiles.map(f => f.name));
      setSelectedFiles(prev => [...prev, ...newFiles]);
      onFilesChange([...selectedFiles, ...newFiles]);
    }
  }, [selectedFiles, onFilesChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true
  });


  return (
    <div className="image-section">
      <div 
        {...getRootProps()} 
        className={`dropzone-area ${isDragActive ? 'active' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="dropzone-content">
          <Upload className="upload-icon" size={20} />
          {isDragActive ? (
            <span className="drop-text">Drop images here ...</span>
          ) : (
            <>
              <span className="upload-text">Drag & drop product images</span>
              <span className="upload-text-sub">or click to select</span>
            </>
          )}
          <small className="file-info">Supports JPG, PNG (up to 5MB)</small>
        </div>
      </div>

      {validImages.length > 0 && (
        <div className="preview-grid">
          {validImages.map((image, index) => (
            <div key={`${image.isPreview ? 'preview' : 'existing'}-${index}`} 
                className={`preview-item ${image.isPreview ? 'preview-new' : ''}`}>
              <img 
                src={getImageUrl(image)}
                alt={`Product preview ${index + 1}`}
                onError={(e) => {
                  e.currentTarget.src = '/fallback.png';
                }}
              />
              {onDeleteImage && (
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => {
                    if (image.isPreview) {
                      // For previews, use the fileName as identifier for parent to remove
                      logger.log(`[ProductImageUpload] Remove preview clicked for:`, image.fileName);
                      onDeleteImage && onDeleteImage(image.fileName || `preview-${index}`);
                    } else {
                      // For existing images, call the parent delete handler
                      logger.log(`[ProductImageUpload] Delete button clicked for image:`, image);
                      onDeleteImage(image.filePath || '');
                    }
                  }}
                  title="Remove image"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(ProductImageUpload, (prevProps, nextProps) => {
  // Only re-render if these specific things change
  return (
    prevProps.bucketUrl === nextProps.bucketUrl &&
    JSON.stringify(prevProps.existingImages) === JSON.stringify(nextProps.existingImages) &&
    prevProps.onFilesChange === nextProps.onFilesChange &&
    prevProps.onDeleteImage === nextProps.onDeleteImage
  );
});